import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useWorkspacePerms } from "@/lib/useWorkspacePerms";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { UserSquare2, Plus, Briefcase, Calendar, Wallet, IdCard, Save, Sparkles, ShieldCheck, Pencil, Loader2, Clock } from "lucide-react";
import DocsComplianceDialog from "@/components/employee/DocsComplianceDialog";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { fmtRelative } from "@/lib/format";
import AdvancedTable from "@/components/table/AdvancedTable";
import CallButton from "@/components/CallButton";
import { EmployeeDesignationPropagationDialog } from "@/pages/app/hr/DesignationPropagationDialog";

const EMPLOYMENT_TYPES = ["full_time", "part_time", "contract", "intern"];
const STATUSES = ["active", "onboarding", "notice", "inactive"];
const CURRENCIES = ["INR", "USD", "EUR", "GBP", "AUD", "CAD", "AED", "SGD"];
const PAY_CYCLES = ["monthly", "biweekly", "weekly"];

export default function Employees() {
  const { activeWorkspaceId, memberships } = useAuth();
  const { can } = useWorkspacePerms();
  const _membership = memberships?.find((m) => m.workspace_id === activeWorkspaceId);
  const canManage = ["owner", "admin", "workspace_owner", "workspace_admin"].includes(_membership?.role) || can("employee.manage");
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showCompliance, setShowCompliance] = useState(false);
  const [docsByUser, setDocsByUser] = useState({});  // R148g · user_id → {uploaded_pct, verified_pct, expiring_soon, expired}
  // Iter Feb2026 · Bulk-edit selection state (multi-select checkboxes).
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkShiftOpen, setBulkShiftOpen] = useState(false);

  const load = async () => {
    if (!activeWorkspaceId) return;
    setBusy(true);
    try {
      const { data } = await api.get(`/workspaces/${activeWorkspaceId}/employees`);
      setRows(data || []);
      // Fetch docs compliance in parallel (silent — some roles can't see it).
      try {
        const { data: comp } = await api.get(`/workspaces/${activeWorkspaceId}/employee-documents/compliance`);
        const by = {};
        for (const r of (comp?.rows || [])) by[r.user_id] = r;
        setDocsByUser(by);
      } catch { /* insufficient perm — chips just show "—" */ }
    } catch (e) {
      if (e?.response?.status === 403) toast.error("Only workspace owner/admin can access Employees");
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); }, [activeWorkspaceId]);

  const columns = [
    { key: "emp_id", label: "Emp ID", width: 100, type: "text", sortable: true, filterType: "text" },
    { key: "full_name", label: "Name", width: 220, sortable: true, filterType: "text",
      render: (r) => {
        // R148m · Feb 2026 — Compliance blocker red dot when the server-side
        // check flagged missing statutory ID or emergency contact.
        const gap = r.compliance_gap || [];
        const gapLabels = {
          statutory_id: "statutory ID (PAN / UAN / National ID)",
          emergency_contact: "emergency contact",
        };
        return (
          <div className="min-w-0">
            <div className="font-semibold truncate flex items-center gap-1.5">
              {gap.length > 0 && (
                <span
                  className="inline-block w-2 h-2 rounded-full bg-rose-600 shrink-0"
                  title={`Missing: ${gap.map((g) => gapLabels[g] || g).join(" · ")}`}
                  data-testid={`employee-compliance-dot-${r.id}`}
                />
              )}
              <span className="truncate">{r.full_name}</span>
            </div>
            <div className="text-xs text-muted-foreground truncate">{r.designation || "—"}</div>
          </div>
        );
      },
    },
    { key: "designation", label: "Designation", width: 160, sortable: true, filterType: "text", groupable: true, visible: false },
    { key: "department", label: "Department", width: 160, sortable: true, filterType: "text", groupable: true },
    { key: "reporting_manager_name", label: "Reports to", width: 160, visible: false,
      accessor: (r) => r.reporting_manager?.name || r.reporting_manager?.full_name || "",
      render: (r) => <span className="text-xs">{r.reporting_manager?.name || r.reporting_manager?.full_name || "—"}</span>,
    },
    { key: "employment_type", label: "Employment", width: 130, sortable: true, groupable: true, filterType: "select",
      filterOptions: EMPLOYMENT_TYPES.map((v) => ({ value: v, label: v.replace(/_/g, " ") })),
      render: (r) => <span className="text-xs">{(r.employment_type || "—").replace(/_/g, " ")}</span>,
    },
    { key: "joining_date", label: "Joined", width: 140, sortable: true, filterType: "date-range",
      render: (r) => <span className="text-xs">{r.joining_date || "—"}{r.joining_date ? ` · ${fmtRelative(r.joining_date)}` : ""}</span>,
    },
    { key: "probation_end_date", label: "Probation end", width: 130, sortable: true, visible: false, filterType: "date-range" },
    { key: "confirmation_date",  label: "Confirmed",     width: 130, sortable: true, visible: false, filterType: "date-range" },
    { key: "exit_date",          label: "Exit",          width: 120, sortable: true, visible: false, filterType: "date-range" },
    { key: "exit_reason",        label: "Exit reason",   width: 160, visible: false, filterType: "text" },
    { key: "status", label: "Status", width: 110, sortable: true, groupable: true, filterType: "select",
      filterOptions: STATUSES.map((v) => ({ value: v, label: v })),
      render: (r) => <span className="chip text-[10px]">{r.status}</span>,
    },
    { key: "work_email",    label: "Work email",   width: 220, visible: false, filterType: "text" },
    { key: "phone",         label: "Phone",        width: 140, visible: false, filterType: "text" },
    { key: "personal_email", label: "Personal email", width: 200, visible: false, filterType: "text" },
    { key: "official_mobile", label: "Official mobile", width: 140, visible: false, filterType: "text" },
    { key: "personal_mobile", label: "Personal mobile", width: 140, visible: false, filterType: "text" },
    { key: "date_of_birth", label: "DOB",          width: 120, visible: false, filterType: "date-range" },
    { key: "gender",        label: "Gender",       width: 90,  visible: false, groupable: true },
    { key: "marital_status", label: "Marital status", width: 110, visible: false, groupable: true },
    { key: "nationality",   label: "Nationality",  width: 110, visible: false, groupable: true },
    { key: "blood_group",   label: "Blood",        width: 80,  visible: false },
    { key: "national_id",   label: "National ID",  width: 140, visible: false },
    { key: "pan",           label: "PAN",          width: 120, visible: false },
    { key: "pf_no",         label: "PF #",         width: 120, visible: false },
    { key: "current_address",   label: "Current addr",   width: 220, visible: false },
    { key: "permanent_address", label: "Permanent addr", width: 220, visible: false },
    { key: "emergency_contact_name",   label: "Emerg contact",     width: 160, visible: false },
    { key: "emergency_contact_phone",  label: "Emerg phone",       width: 140, visible: false },
    { key: "emergency_contact_relation", label: "Emerg relation",   width: 120, visible: false },
    { key: "salary_amount", label: "CTC",          width: 120, visible: false, sortable: true, align: "right",
      render: (r) => r.salary_amount != null ? <span className="tabular-nums text-xs">{r.salary_currency || ""} {Number(r.salary_amount).toLocaleString()}</span> : <span className="text-muted-foreground">—</span>,
    },
    { key: "salary_currency", label: "Currency",   width: 90,  visible: false },
    { key: "pay_cycle",       label: "Pay cycle",  width: 100, visible: false, groupable: true },
    { key: "bank_name",       label: "Bank",       width: 150, visible: false },
    { key: "bank_account_number", label: "Bank a/c", width: 160, visible: false },
    { key: "bank_ifsc",       label: "IFSC",       width: 100, visible: false },
    { key: "leave_balance_casual", label: "CL",    width: 70,  visible: false, align: "right" },
    { key: "leave_balance_sick",   label: "SL",    width: 70,  visible: false, align: "right" },
    { key: "leave_balance_earned", label: "EL",    width: 70,  visible: false, align: "right" },
    { key: "wedding_anniversary", label: "Anniversary", width: 130, visible: false, filterType: "date-range" },
    { key: "user_linked", label: "User", width: 90, align: "right",
      accessor: (r) => r.user ? "linked" : "unlinked",
      render: (r) => r.user ? <span className="chip chip-internal text-[10px]">linked</span> : <span className="text-foreground/60">—</span>,
    },
    // R148g · Feb 2026 — Docs completeness chip. Colour tracks upload
    // percentage: emerald ≥95 · amber ≥50 · rose <50. Shows expired /
    // expiring counts in the tooltip so HR can spot laggards without
    // opening each profile.
    { key: "docs_completeness", label: "Docs %", width: 100, align: "center", sortable: true,
      accessor: (r) => docsByUser[r.user_id]?.uploaded_pct ?? -1,
      render: (r) => {
        const d = docsByUser[r.user_id];
        if (!d || !d.total) return <span className="text-foreground/40 text-[10px]">—</span>;
        const p = d.uploaded_pct || 0;
        const cls = p >= 95 ? "border-emerald-500 bg-emerald-50 text-emerald-700"
                : p >= 50 ? "border-amber-500 bg-amber-50 text-amber-700"
                : "border-rose-500 bg-rose-50 text-rose-700";
        const tip = `${d.uploaded}/${d.total} uploaded · ${d.verified}/${d.total} verified` +
          (d.expiring_soon ? ` · ${d.expiring_soon} expiring soon` : "") +
          (d.expired ? ` · ${d.expired} expired` : "");
        return (
          <span
            className={`inline-block border px-1.5 text-[10px] font-mono font-bold ${cls}`}
            title={tip}
            data-testid={`emp-docs-pct-${r.id}`}
          >
            {p}%
            {d.expired > 0 && <span className="ml-1 text-rose-700">!</span>}
          </span>
        );
      },
    },
    { key: "call", label: "Call", width: 70, align: "center",
      render: (r) => {
        const phone = r.phone || r.official_mobile || r.personal_mobile;
        if (!phone) return <span className="text-foreground/40">—</span>;
        return (
          <CallButton phone={phone} customer_name={r.full_name}
            entity_type="employee" entity_id={r.id} entity_name={r.full_name}
            className="rounded-none h-7 px-1.5"
            testid={`employee-call-${r.id}`} />
        );
      },
    },
    // R148g · Feb 2026 — New editable fields exposed as togglable
    // columns. All hidden by default (visible: false) so the default
    // Employees list stays lean, but HR can pick them from the Columns
    // menu to build any custom report / audit view.
    { key: "father_name",            label: "Father's name",       width: 160, sortable: true, visible: false, accessor: (r) => r.father_name || "", render: (r) => r.father_name || <span className="text-foreground/40">—</span> },
    { key: "mother_name",            label: "Mother's name",       width: 160, sortable: true, visible: false, accessor: (r) => r.mother_name || "", render: (r) => r.mother_name || <span className="text-foreground/40">—</span> },
    { key: "spouse_name",            label: "Spouse name",         width: 160, sortable: true, visible: false, accessor: (r) => r.spouse_name || "", render: (r) => r.spouse_name || <span className="text-foreground/40">—</span> },
    { key: "spouse_dob",             label: "Spouse DOB",          width: 120, sortable: true, visible: false, type: "date", accessor: (r) => r.spouse_dob || "", render: (r) => r.spouse_dob?.slice(0,10) || <span className="text-foreground/40">—</span> },
    { key: "dependent_count",        label: "# dependents",        width: 100, sortable: true, visible: false, align: "right", type: "number", accessor: (r) => r.dependent_count || 0, render: (r) => r.dependent_count ?? <span className="text-foreground/40">—</span> },
    { key: "uan",                    label: "UAN",                 width: 140, sortable: true, visible: false, accessor: (r) => r.uan || "", render: (r) => <span className="font-mono text-[11px]">{r.uan || "—"}</span> },
    { key: "esi_no",                 label: "ESI #",               width: 140, sortable: true, visible: false, accessor: (r) => r.esi_no || "", render: (r) => <span className="font-mono text-[11px]">{r.esi_no || "—"}</span> },
    { key: "tan",                    label: "TAN",                 width: 140, sortable: true, visible: false, accessor: (r) => r.tan || "", render: (r) => <span className="font-mono text-[11px]">{r.tan || "—"}</span> },
    { key: "passport_no",            label: "Passport #",          width: 140, sortable: true, visible: false, accessor: (r) => r.passport_no || "", render: (r) => <span className="font-mono text-[11px]">{r.passport_no || "—"}</span> },
    { key: "passport_expiry",        label: "Passport expiry",     width: 130, sortable: true, visible: false, type: "date", accessor: (r) => r.passport_expiry || "", render: (r) => r.passport_expiry?.slice(0,10) || <span className="text-foreground/40">—</span> },
    { key: "driving_license_no",     label: "DL #",                width: 140, sortable: true, visible: false, accessor: (r) => r.driving_license_no || "", render: (r) => <span className="font-mono text-[11px]">{r.driving_license_no || "—"}</span> },
    { key: "previous_employer",      label: "Last employer",       width: 180, sortable: true, visible: false, accessor: (r) => r.previous_employer || "", render: (r) => r.previous_employer || <span className="text-foreground/40">—</span> },
    { key: "previous_designation",   label: "Last designation",    width: 180, sortable: true, visible: false, accessor: (r) => r.previous_designation || "", render: (r) => r.previous_designation || <span className="text-foreground/40">—</span> },
    { key: "previous_ctc",           label: "Last CTC",            width: 120, sortable: true, visible: false, align: "right", type: "number", accessor: (r) => r.previous_ctc || 0, render: (r) => r.previous_ctc ? <span className="font-mono text-[11px]">{Number(r.previous_ctc).toLocaleString("en-IN")}</span> : <span className="text-foreground/40">—</span> },
    { key: "notice_period_days",     label: "Notice (days)",       width: 110, sortable: true, visible: false, align: "right", type: "number", accessor: (r) => r.notice_period_days || 0, render: (r) => r.notice_period_days ?? <span className="text-foreground/40">—</span> },
    { key: "total_experience_years", label: "Experience (yrs)",    width: 130, sortable: true, visible: false, align: "right", type: "number", accessor: (r) => r.total_experience_years || 0, render: (r) => r.total_experience_years ?? <span className="text-foreground/40">—</span> },
    { key: "reference_contact",      label: "Reference contact",   width: 180, sortable: true, visible: false, accessor: (r) => r.reference_contact || "", render: (r) => r.reference_contact || <span className="text-foreground/40">—</span> },
    { key: "notice_period_reason",   label: "Notice reason",       width: 200, sortable: true, visible: false, accessor: (r) => r.notice_period_reason || "", render: (r) => <span className="text-[11px] line-clamp-2" title={r.notice_period_reason}>{r.notice_period_reason || "—"}</span> },
  ];

  return (
    <>
      <PageHeader
        eyebrow="HR"
        title="Employees"
        description="Master record for every team member. Link to user accounts to grant app access."
        testid="employees-header"
        actions={canManage && (
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              className="rounded-none"
              onClick={() => setShowCompliance(true)}
              data-testid="employees-docs-compliance-btn"
              title="Workspace-wide employee documents compliance dashboard"
            >
              <ShieldCheck className="w-4 h-4 mr-2" /> Docs compliance
            </Button>
            <Button className="rounded-none" onClick={() => setCreating(true)} data-testid="employees-new-btn">
              <Plus className="w-4 h-4 mr-2" /> Add employee
            </Button>
          </div>
        )}
      />
      <div className="px-4 sm:px-6 md:px-10 py-6" data-testid="employees-list">
        <AdvancedTable
          tableKey="employees"
          title="Employees"
          rows={rows}
          columns={columns}
          loading={busy && rows.length === 0}
          emptyIcon={UserSquare2}
          emptyTitle="No employees yet"
          emptyDescription="Add your first employee to start building the master record."
          onRowClick={(r) => navigate(`/app/employees/${r.id}`)}
          selectable={canManage}
          selectedIds={selectedIds}
          onSelectionChange={setSelectedIds}
          renderBulkBar={(selectedRows) => (
            <>
              <Button
                size="sm"
                variant="outline"
                className="rounded-none h-7"
                onClick={() => setBulkOpen(true)}
                data-testid="employees-bulk-edit-btn"
              >
                <Pencil className="w-3.5 h-3.5 mr-1" />
                Bulk edit {selectedRows.length}
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="rounded-none h-7"
                onClick={() => setBulkShiftOpen(true)}
                data-testid="employees-bulk-shift-btn"
                title="Assign a shift to the selected employees"
              >
                <Clock className="w-3.5 h-3.5 mr-1" />
                Set shift…
              </Button>
            </>
          )}
        />
      </div>
      {bulkOpen && canManage && (
        <EmployeesBulkEditDialog
          wsId={activeWorkspaceId}
          selectedIds={Array.from(selectedIds)}
          onClose={() => setBulkOpen(false)}
          onSaved={async () => {
            setBulkOpen(false);
            setSelectedIds(new Set());
            await load();
          }}
        />
      )}
      {bulkShiftOpen && canManage && (
        <EmployeesBulkShiftDialog
          wsId={activeWorkspaceId}
          selectedRows={rows.filter((r) => selectedIds.has(r.id))}
          onClose={() => setBulkShiftOpen(false)}
          onSaved={async () => {
            setBulkShiftOpen(false);
            setSelectedIds(new Set());
            await load();
          }}
        />
      )}

      {creating && (
        <EmployeeDialog
          workspaceMembers={undefined}
          employee={{}}
          onClose={() => setCreating(false)}
          onSaved={async (created) => {
            setCreating(false);
            if (created?.id) {
              navigate(`/app/employees/${created.id}`);
            } else {
              await load();
            }
          }}
        />
      )}
      {showCompliance && (
        <DocsComplianceDialog wsId={activeWorkspaceId} onClose={() => setShowCompliance(false)} />
      )}
    </>
  );
}

function EmployeeDialog({ employee, onClose, onSaved }) {
  const { activeWorkspaceId } = useAuth();
  const isNew = !employee?.id;
  const [members, setMembers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [linkUserOpen, setLinkUserOpen] = useState(false);
  const [form, setForm] = useState({
    emp_id: employee.emp_id || "",
    full_name: employee.full_name || "",
    designation: employee.designation || "",
    designation_id: employee.designation_id || "",
    department: employee.department || "",
    reporting_manager_id: employee.reporting_manager_id || "",
    joining_date: employee.joining_date || "",
    employment_type: employee.employment_type || "",
    status: employee.status || "active",
    work_email: employee.work_email || "",
    phone: employee.phone || "",
    address: employee.address || "",
    user_id: employee.user_id || "",
    // Personal
    date_of_birth: employee.date_of_birth || "",
    wedding_anniversary: employee.wedding_anniversary || "",
    gender: employee.gender || "",
    marital_status: employee.marital_status || "",
    nationality: employee.nationality || "",
    blood_group: employee.blood_group || "",
    current_address: employee.current_address || "",
    permanent_address: employee.permanent_address || "",
    // Emergency
    emergency_contact_name: employee.emergency_contact_name || "",
    emergency_contact_relation: employee.emergency_contact_relation || "",
    emergency_contact_phone: employee.emergency_contact_phone || "",
    emergency_contact_email: employee.emergency_contact_email || "",
    // HRMS extras
    probation_end_date: employee.probation_end_date || "",
    confirmation_date: employee.confirmation_date || "",
    exit_date: employee.exit_date || "",
    exit_reason: employee.exit_reason || "",
    on_notice_until: employee.on_notice_until || "",
    national_id: employee.national_id || "",
    leave_balance_sick: employee.leave_balance_sick ?? "",
    // Iter 91 — Intelligent Onboarding (only relevant on create)
    create_user_login: false,
    role_id: "",
    send_welcome_email: true,
    welcome_template_id: "",
    buddy_user_id: "",
    leave_balance_casual: employee.leave_balance_casual ?? "",
    leave_balance_earned: employee.leave_balance_earned ?? "",
    salary_amount: employee.salary_amount ?? "",
    salary_currency: employee.salary_currency || "",
    pay_cycle: employee.pay_cycle || "",
    bank_name: employee.bank_name || "",
    bank_account_holder: employee.bank_account_holder || "",
    bank_account_number: employee.bank_account_number || "",
    bank_ifsc: employee.bank_ifsc || "",
    pan: employee.pan || "",
    pf_no: employee.pf_no || "",
    // R148g · Feb 2026 — New sections seeded from the existing document
    father_name: employee.father_name || "",
    mother_name: employee.mother_name || "",
    spouse_name: employee.spouse_name || "",
    spouse_dob: employee.spouse_dob || "",
    dependent_count: employee.dependent_count ?? "",
    dependent_details: employee.dependent_details || "",
    uan: employee.uan || "",
    esi_no: employee.esi_no || "",
    tan: employee.tan || "",
    passport_no: employee.passport_no || "",
    passport_expiry: employee.passport_expiry || "",
    driving_license_no: employee.driving_license_no || "",
    previous_employer: employee.previous_employer || "",
    previous_designation: employee.previous_designation || "",
    previous_ctc: employee.previous_ctc ?? "",
    notice_period_days: employee.notice_period_days ?? "",
    total_experience_years: employee.total_experience_years ?? "",
    reference_contact: employee.reference_contact || "",
    notice_period_reason: employee.notice_period_reason || "",
    // Round-trip custom fields as "key: value" per line.
    custom_fields_text: employee.custom_fields
      ? Object.entries(employee.custom_fields).map(([k, v]) => `${k}: ${v}`).join("\n")
      : "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  // Iter Feb 2026 R19 · Propagation dialog state (opened after save when
  // the designation actually changes and the employee has a linked user).
  const [propOpen, setPropOpen] = useState(false);
  const [propCtx, setPropCtx] = useState(null);   // { userId, oldDesignation, newDesignation }

  useEffect(() => {
    if (!activeWorkspaceId) return;
    api.get(`/workspaces/${activeWorkspaceId}/members`).then((r) => setMembers(r.data || [])).catch(() => setMembers([]));
    api.get(`/workspaces/${activeWorkspaceId}/roles`).then((r) => setRoles(r.data || [])).catch(() => setRoles([]));
    api.get(`/workspaces/${activeWorkspaceId}/departments`).then((r) => setDepartments(r.data || [])).catch(() => setDepartments([]));
    // Iter Feb 2026 R19 · Fetch the workspace's HR Designations master
    // (with duty templates + JDs) so HR can pick a canonical value and
    // auto-assign job duties on save.
    api.get(`/workspaces/${activeWorkspaceId}/hr/designations`)
      .then((r) => setDesignations(r.data || []))
      .catch(() => setDesignations([]));
    api.get(`/workspaces/${activeWorkspaceId}/comms/templates`).then((r) => setEmailTemplates(r.data?.rows || [])).catch(() => setEmailTemplates([]));
  }, [activeWorkspaceId]);

  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...form };
      ["leave_balance_sick", "leave_balance_casual", "leave_balance_earned", "salary_amount",
        // R148g · Feb 2026 — Numeric fields from the new sections.
        "dependent_count", "previous_ctc", "notice_period_days", "total_experience_years",
      ].forEach((k) => {
        if (payload[k] === "") payload[k] = null;
        else if (payload[k] != null) payload[k] = Number(payload[k]);
      });
      // R148g · Parse `custom_fields_text` (one `key: value` per line)
      // into a dict so the backend can index individual fields later.
      if (typeof payload.custom_fields_text === "string") {
        const dict = {};
        payload.custom_fields_text.split(/\r?\n/).forEach((line) => {
          const m = line.match(/^\s*([^:]+?)\s*:\s*(.+?)\s*$/);
          if (m) dict[m[1]] = m[2];
        });
        payload.custom_fields = dict;
        delete payload.custom_fields_text;
      }
      ["reporting_manager_id", "user_id"].forEach((k) => { if (!payload[k]) payload[k] = null; });
      // Iter Feb 2026 R19 · Detect designation change so we can open the
      // propagation dialog after save (only for existing employees with a
      // linked user_id — new employees inherit on first duty-chart open).
      const oldDid = employee.designation_id || null;
      const newDid = payload.designation_id || null;
      const designationChanged = !isNew && oldDid !== newDid && !!employee.user_id;

      let savedEmp;
      if (isNew) {
        const { data } = await api.post(`/workspaces/${activeWorkspaceId}/employees`, payload);
        savedEmp = data;
        if (data?._created_user && !data._created_user.existed) {
          toast.success(`Employee added — Acquilix login created for ${data._created_user.email}`);
        } else if (data?._journey_id) {
          toast.success("Employee added — onboarding journey started");
        } else {
          toast.success("Employee added");
        }
        await onSaved?.(data);
      } else {
        await api.patch(`/employees/${employee.id}`, payload);
        savedEmp = { ...employee, ...payload };
        toast.success("Employee updated");
      }

      if (designationChanged) {
        const oldD = designations.find((d) => d.id === oldDid);
        const newD = designations.find((d) => d.id === newDid);
        setPropCtx({
          userId: employee.user_id,
          oldDesignation: oldD ? { id: oldD.id, name: oldD.name } : (employee.designation ? { id: oldDid, name: employee.designation } : null),
          newDesignation: newD ? { id: newD.id, name: newD.name } : null,
        });
        setPropOpen(true);
        // Defer onSaved until dialog closes so the parent re-fetches AFTER duty reconciliation
      } else if (!isNew) {
        await onSaved?.();
      }
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-auto" data-testid="employee-dialog">
        <DialogHeader>
          <DialogTitle>{isNew ? "Add employee" : `Edit ${employee.full_name}`}</DialogTitle>
        </DialogHeader>
        <div className="space-y-5">
          <Section icon={Briefcase} title="Identity & role">
            <Row3>
              <Field label="Employee ID" value={form.emp_id} onChange={(v) => set("emp_id", v)} testid="emp-empid" placeholder="EMP-001" />
              <Field label="Full name *" value={form.full_name} onChange={(v) => set("full_name", v)} testid="emp-name" />
              <Field label="Work email" type="email" value={form.work_email} onChange={(v) => set("work_email", v)} testid="emp-email" />
            </Row3>
            <Row3>
              <div>
                <Label className="label-eyebrow text-[10px] mb-1 inline-block">Designation</Label>
                <select
                  value={form.designation_id || ""}
                  onChange={(e) => {
                    const did = e.target.value;
                    const d = designations.find((x) => x.id === did);
                    setForm((s) => ({ ...s, designation_id: did, designation: d?.name || "" }));
                  }}
                  className="h-9 w-full border border-border rounded-none px-2 text-xs bg-background"
                  data-testid="emp-designation"
                >
                  <option value="">— Select designation —</option>
                  {designations.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name}
                      {d.default_duties?.length ? ` · ${d.default_duties.length} duties` : ""}
                    </option>
                  ))}
                  {/* Handle a legacy free-text designation that isn't in the master list */}
                  {form.designation && !form.designation_id && (
                    <option value="" disabled>{form.designation} (legacy custom · pick canonical)</option>
                  )}
                </select>
                {designations.length === 0 && (
                  <p className="text-[9px] text-muted-foreground mt-1">
                    No designations configured. Add them under <a href="/app/hr/designations" className="text-primary hover:underline">HR → Designations</a>.
                  </p>
                )}
                {form.designation_id && (
                  <p className="text-[9px] text-muted-foreground mt-1">
                    Job duties from this designation will be assigned on save.
                  </p>
                )}
              </div>
              <div>
                <Label className="label-eyebrow text-[10px] mb-1 inline-block">Department</Label>
                <select
                  value={form.department}
                  onChange={(e) => {
                    if (e.target.value === "__new__") {
                      const name = window.prompt("New department name?");
                      const clean = (name || "").trim();
                      if (clean.length < 2) {
                        if (clean) toast.error("Department name must be at least 2 characters");
                        return;
                      }
                      api.post(`/workspaces/${activeWorkspaceId}/departments`, { name: clean })
                        .then(({ data }) => {
                          setDepartments((s) => [...s, data]);
                          set("department", data.name);
                          toast.success("Department created");
                        })
                        .catch((err) => toast.error(apiErrorMessage(err, "Failed to create department")));
                      return;
                    }
                    set("department", e.target.value);
                  }}
                  className="h-9 w-full border border-border rounded-none px-2 text-xs bg-background"
                  data-testid="emp-department"
                >
                  <option value="">— Select department —</option>
                  {departments.map((d) => <option key={d.id} value={d.name}>{d.name}</option>)}
                  {/* Handle a legacy free-text department that isn't in the list */}
                  {form.department && !departments.find((d) => d.name === form.department) && (
                    <option value={form.department}>{form.department} (custom)</option>
                  )}
                  <option value="__new__">＋ Create new department…</option>
                </select>
              </div>
              <Field label="Phone" value={form.phone} onChange={(v) => set("phone", v)} testid="emp-phone" />
            </Row3>
            <Row3>
              <SelectField label="Employment type" value={form.employment_type} onChange={(v) => set("employment_type", v)} options={EMPLOYMENT_TYPES.map((t) => ({ v: t, label: t.replace("_", " ") }))} testid="emp-employment-type" />
              <SelectField label="Status" value={form.status} onChange={(v) => set("status", v)} options={STATUSES.map((s) => ({ v: s, label: s }))} testid="emp-status" />
              <SelectField
                label="Reporting manager"
                value={form.reporting_manager_id}
                onChange={(v) => set("reporting_manager_id", v)}
                options={members.map((m) => ({ v: m.user_id, label: m.user?.name || m.user?.email || "—" }))}
                testid="emp-manager"
              />
            </Row3>
            <Row2>
              <Field label="Joining date" type="date" value={form.joining_date?.slice(0, 10) || ""} onChange={(v) => set("joining_date", v)} testid="emp-joining" />
              <div>
                <SelectField
                  label="Linked user account (grants app access)"
                  value={form.user_id}
                  onChange={(v) => set("user_id", v)}
                  options={members.map((m) => ({ v: m.user_id, label: m.user?.name || m.user?.email || "—" }))}
                  testid="emp-user-link"
                />
                {!isNew && !form.user_id && (
                  <button
                    type="button"
                    className="mt-1.5 text-[11px] text-primary hover:underline inline-flex items-center gap-1"
                    onClick={() => setLinkUserOpen(true)}
                    data-testid="emp-create-link-user-btn"
                  >
                    <Sparkles className="w-3 h-3" /> Create &amp; link a new user account for this employee
                  </button>
                )}
              </div>
            </Row2>
            <Field label="Address" value={form.address} onChange={(v) => set("address", v)} testid="emp-address" placeholder="Postal address" />
          </Section>

          {isNew && (
            <Section icon={Sparkles} title="Intelligent onboarding">
              <p className="text-[11px] text-muted-foreground">
                Turning on <b>Create Acquilix login</b> auto-provisions the user, starts an onboarding journey with a 14-step checklist, and (optionally) sends a beautifully-templated welcome email — all in one click.
              </p>
              <div className="flex items-center gap-3 flex-wrap">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={!!form.create_user_login} onChange={(e) => set("create_user_login", e.target.checked)} data-testid="emp-create-login" />
                  <span>Create Acquilix login</span>
                </label>
                <label className={`flex items-center gap-2 text-sm cursor-pointer ${!form.create_user_login ? "opacity-50" : ""}`}>
                  <input type="checkbox" checked={!!form.send_welcome_email} disabled={!form.create_user_login} onChange={(e) => set("send_welcome_email", e.target.checked)} data-testid="emp-send-welcome" />
                  <span>Send welcome email to <span className="font-mono text-[11px]">{form.work_email || "work_email"}</span></span>
                </label>
              </div>
              {form.create_user_login && (
                <>
                  <Row2>
                    <SelectField label="Role to assign" value={form.role_id} onChange={(v) => set("role_id", v)}
                      options={roles.map((r) => ({ v: r.id, label: r.name || r.slug }))} testid="emp-role" />
                    <SelectField label="Buddy (peer mentor)" value={form.buddy_user_id} onChange={(v) => set("buddy_user_id", v)}
                      options={members.map((m) => ({ v: m.user_id, label: m.user?.name || m.user?.email || "—" }))} testid="emp-buddy" />
                  </Row2>
                  {form.send_welcome_email && (
                    <Row2>
                      <SelectField
                        label="Welcome email template"
                        value={form.welcome_template_id}
                        onChange={(v) => set("welcome_template_id", v)}
                        options={emailTemplates.map((t) => ({ v: t.id, label: t.name + (t.subject ? ` · ${t.subject.slice(0, 40)}` : "") }))}
                        placeholder="Default onboarding template"
                        testid="emp-welcome-template"
                      />
                      <div className="text-[11px] text-muted-foreground self-end pb-2">
                        Manage templates in <a href="/app/admin/comms" target="_blank" rel="noreferrer" className="text-primary hover:underline">Communications Command Center → Templates</a>. Leave empty to use the onboarding template&apos;s default.
                      </div>
                    </Row2>
                  )}
                </>
              )}
            </Section>
          )}

          <Section icon={Briefcase} title="Personal">
            <Row3>
              <Field label="Date of birth" type="date" value={form.date_of_birth?.slice(0, 10) || ""} onChange={(v) => set("date_of_birth", v)} testid="emp-dob" />
              <SelectField label="Gender" value={form.gender} onChange={(v) => set("gender", v)} options={[{v:"male",label:"Male"},{v:"female",label:"Female"},{v:"other",label:"Other"},{v:"prefer_not",label:"Prefer not to say"}]} testid="emp-gender" />
              <SelectField label="Marital status" value={form.marital_status} onChange={(v) => set("marital_status", v)} options={[{v:"single",label:"Single"},{v:"married",label:"Married"},{v:"divorced",label:"Divorced"},{v:"widowed",label:"Widowed"}]} testid="emp-marital" />
            </Row3>
            <Row3>
              <Field label="Nationality" value={form.nationality} onChange={(v) => set("nationality", v)} testid="emp-nationality" />
              <Field label="Blood group" value={form.blood_group} onChange={(v) => set("blood_group", v)} testid="emp-blood" placeholder="O+" />
              <Field label="National ID / Aadhaar / SSN" value={form.national_id} onChange={(v) => set("national_id", v)} testid="emp-national-id" />
            </Row3>
            <Row3>
              <Field label="Wedding anniversary" type="date" value={form.wedding_anniversary?.slice(0, 10) || ""} onChange={(v) => set("wedding_anniversary", v)} testid="emp-wedding-anniversary" />
              <div /><div />
            </Row3>
            <Field label="Current address" value={form.current_address} onChange={(v) => set("current_address", v)} testid="emp-current-address" placeholder="Where they live today" />
            <Field label="Permanent address" value={form.permanent_address} onChange={(v) => set("permanent_address", v)} testid="emp-perm-address" placeholder="Home / permanent address" />
          </Section>

          <Section icon={Briefcase} title="Emergency contact">
            <Row3>
              <Field label="Name" value={form.emergency_contact_name} onChange={(v) => set("emergency_contact_name", v)} testid="emp-em-name" />
              <Field label="Relationship" value={form.emergency_contact_relation} onChange={(v) => set("emergency_contact_relation", v)} testid="emp-em-relation" placeholder="Spouse, parent, sibling…" />
              <Field label="Phone" value={form.emergency_contact_phone} onChange={(v) => set("emergency_contact_phone", v)} testid="emp-em-phone" />
            </Row3>
            <Field label="Email (optional)" type="email" value={form.emergency_contact_email} onChange={(v) => set("emergency_contact_email", v)} testid="emp-em-email" />
          </Section>

          {/* R148g · Feb 2026 — Family & dependents. HR uses these for
              insurance, tax exemptions, and payroll rules. */}
          <Section icon={Briefcase} title="Family & dependents">
            <Row3>
              <Field label="Father's name" value={form.father_name} onChange={(v) => set("father_name", v)} testid="emp-father-name" />
              <Field label="Mother's name" value={form.mother_name} onChange={(v) => set("mother_name", v)} testid="emp-mother-name" />
              <Field label="Spouse name" value={form.spouse_name} onChange={(v) => set("spouse_name", v)} testid="emp-spouse-name" />
            </Row3>
            <Row3>
              <Field label="Spouse DOB" type="date" value={form.spouse_dob?.slice(0, 10) || ""} onChange={(v) => set("spouse_dob", v)} testid="emp-spouse-dob" />
              <Field label="Number of children" type="number" value={form.dependent_count} onChange={(v) => set("dependent_count", v)} testid="emp-dependent-count" placeholder="0" />
              <div />
            </Row3>
            <Field label="Dependent details (name · DOB · relation, one per line)" value={form.dependent_details} onChange={(v) => set("dependent_details", v)} testid="emp-dependent-details" placeholder="Ravi · 2015-03-20 · Son" />
          </Section>

          {/* R148g · Statutory IDs (India-first, tolerant to other geos).
              Existing PAN / PF are already in the Payroll section — this
              block picks up UAN, ESI, TAN, and any other identifiers. */}
          <Section icon={IdCard} title="Statutory IDs">
            <Row3>
              <Field label="UAN (Universal PF Account)" value={form.uan} onChange={(v) => set("uan", v)} testid="emp-uan" />
              <Field label="ESI number" value={form.esi_no} onChange={(v) => set("esi_no", v)} testid="emp-esi-no" />
              <Field label="TAN" value={form.tan} onChange={(v) => set("tan", v)} testid="emp-tan" />
            </Row3>
            <Row3>
              <Field label="Passport number" value={form.passport_no} onChange={(v) => set("passport_no", v)} testid="emp-passport-no" />
              <Field label="Passport expiry" type="date" value={form.passport_expiry?.slice(0, 10) || ""} onChange={(v) => set("passport_expiry", v)} testid="emp-passport-expiry" />
              <Field label="Driving licence #" value={form.driving_license_no} onChange={(v) => set("driving_license_no", v)} testid="emp-dl-no" />
            </Row3>
          </Section>

          {/* R148g · Previous employment. Used by HR for background
              verification, notice-period reconciliation, and reference
              checks. */}
          <Section icon={Briefcase} title="Previous employment">
            <Row3>
              <Field label="Last employer" value={form.previous_employer} onChange={(v) => set("previous_employer", v)} testid="emp-prev-employer" />
              <Field label="Last designation" value={form.previous_designation} onChange={(v) => set("previous_designation", v)} testid="emp-prev-designation" />
              <Field label="Last CTC (annual)" type="number" value={form.previous_ctc} onChange={(v) => set("previous_ctc", v)} testid="emp-prev-ctc" placeholder="800000" />
            </Row3>
            <Row3>
              <Field label="Notice period served (days)" type="number" value={form.notice_period_days} onChange={(v) => set("notice_period_days", v)} testid="emp-notice-days" />
              <Field label="Total experience (years)" type="number" value={form.total_experience_years} onChange={(v) => set("total_experience_years", v)} testid="emp-total-exp" />
              <Field label="Reference contact" value={form.reference_contact} onChange={(v) => set("reference_contact", v)} testid="emp-reference" />
            </Row3>
            <Field label="Notice-period / resignation reason" value={form.notice_period_reason} onChange={(v) => set("notice_period_reason", v)} testid="emp-notice-reason" placeholder="Why they left the last role — used for BGV" />
          </Section>

          {/* R148g · Custom fields — free-form key/value so tenants that
              need something we haven't modelled (blood test date, LinkedIn
              URL, T-shirt size, etc.) can capture it right here. Rendered
              as a textarea; parsed as `key: value` pairs on save. */}
          <Section icon={IdCard} title="Custom fields">
            <Field
              label="One per line, in the form  key: value  (e.g. `T-shirt size: L`)"
              value={form.custom_fields_text}
              onChange={(v) => set("custom_fields_text", v)}
              testid="emp-custom-fields"
              placeholder={"LinkedIn: https://linkedin.com/in/…\nT-shirt size: L"}
            />
          </Section>

          <Section icon={Briefcase} title="Lifecycle">
            <Row3>
              <Field label="Probation ends on" type="date" value={form.probation_end_date?.slice(0, 10) || ""} onChange={(v) => set("probation_end_date", v)} testid="emp-probation-end" />
              <Field label="Confirmation date" type="date" value={form.confirmation_date?.slice(0, 10) || ""} onChange={(v) => set("confirmation_date", v)} testid="emp-confirmation" />
              <Field label="Exit date" type="date" value={form.exit_date?.slice(0, 10) || ""} onChange={(v) => set("exit_date", v)} testid="emp-exit-date" />
            </Row3>
            <Row3>
              <Field label="On notice until" type="date" value={form.on_notice_until?.slice(0, 10) || ""} onChange={(v) => set("on_notice_until", v)} testid="emp-on-notice-until" placeholder="YYYY-MM-DD" />
              <Field label="Exit reason (if applicable)" value={form.exit_reason} onChange={(v) => set("exit_reason", v)} testid="emp-exit-reason" placeholder="Resignation, end of contract, …" />
            </Row3>
          </Section>

          <Section icon={Calendar} title="Leave balance (days)">
            <Row3>
              <Field label="Sick leave" type="number" value={form.leave_balance_sick} onChange={(v) => set("leave_balance_sick", v)} testid="emp-leave-sick" />
              <Field label="Casual leave" type="number" value={form.leave_balance_casual} onChange={(v) => set("leave_balance_casual", v)} testid="emp-leave-casual" />
              <Field label="Earned / privilege leave" type="number" value={form.leave_balance_earned} onChange={(v) => set("leave_balance_earned", v)} testid="emp-leave-earned" />
            </Row3>
          </Section>

          <Section icon={Wallet} title="Payroll (basic)">
            <Row3>
              <Field label="Salary amount" type="number" value={form.salary_amount} onChange={(v) => set("salary_amount", v)} testid="emp-salary" placeholder="50000" />
              <SelectField label="Currency" value={form.salary_currency} onChange={(v) => set("salary_currency", v)} options={CURRENCIES.map((c) => ({ v: c, label: c }))} testid="emp-currency" />
              <SelectField label="Pay cycle" value={form.pay_cycle} onChange={(v) => set("pay_cycle", v)} options={PAY_CYCLES.map((c) => ({ v: c, label: c }))} testid="emp-pay-cycle" />
            </Row3>
            <Row3>
              <Field label="Bank name" value={form.bank_name} onChange={(v) => set("bank_name", v)} testid="emp-bank" />
              <Field label="PAN / Tax ID" value={form.pan} onChange={(v) => set("pan", v)} testid="emp-pan" />
              <Field label="PF / SS no." value={form.pf_no} onChange={(v) => set("pf_no", v)} testid="emp-pf" />
            </Row3>
            <Row3>
              <Field label="Account holder" value={form.bank_account_holder} onChange={(v) => set("bank_account_holder", v)} testid="emp-bank-holder" />
              <Field label="Account number" value={form.bank_account_number} onChange={(v) => set("bank_account_number", v)} testid="emp-bank-acct" />
              <Field label="IFSC / Routing" value={form.bank_ifsc} onChange={(v) => set("bank_ifsc", v)} testid="emp-bank-ifsc" />
            </Row3>
          </Section>
        </div>
        <div className="flex justify-end gap-2 pt-3">
          <Button variant="ghost" className="rounded-none" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy || !form.full_name} className="rounded-none" data-testid="emp-save">
            <Save className="w-4 h-4 mr-2" /> {busy ? "Saving…" : isNew ? "Add employee" : "Save"}
          </Button>
        </div>
      </DialogContent>
      {linkUserOpen && (
        <CreateLinkUserDialog
          wsId={activeWorkspaceId}
          defaultEmail={form.work_email || form.personal_email || ""}
          defaultName={form.full_name}
          roles={roles}
          onClose={() => setLinkUserOpen(false)}
          onCreated={(newUserId, newMember) => {
            set("user_id", newUserId);
            // Refresh members list so the new user appears in the dropdown.
            setMembers((prev) => [...prev, newMember]);
            setLinkUserOpen(false);
          }}
        />
      )}
      {propOpen && propCtx && (
        <EmployeeDesignationPropagationDialog
          open={propOpen}
          onClose={async () => {
            setPropOpen(false);
            setPropCtx(null);
            await onSaved?.();
          }}
          workspaceId={activeWorkspaceId}
          userId={propCtx.userId}
          employeeName={form.full_name}
          oldDesignation={propCtx.oldDesignation}
          newDesignation={propCtx.newDesignation}
          onApplied={async () => {
            setPropOpen(false);
            setPropCtx(null);
            await onSaved?.();
          }}
        />
      )}
    </Dialog>
  );
}

export function CreateLinkUserDialog({ wsId, defaultEmail, defaultName, roles, onClose, onCreated }) {
  const [email, setEmail] = useState(defaultEmail || "");
  const [name, setName] = useState(defaultName || "");
  const [role, setRole] = useState("team_member");
  const [sendInvite, setSendInvite] = useState(true);
  const [busy, setBusy] = useState(false);
  const [tempPassword, setTempPassword] = useState(null);

  const submit = async () => {
    if (!email.trim() || !name.trim()) {
      toast.error("Email and name are both required"); return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/workspaces/${wsId}/users-admin`, {
        email: email.trim().toLowerCase(),
        name: name.trim(),
        role,
        user_type: "employee",
        send_invite: sendInvite,
      });
      if (data.temp_password) {
        setTempPassword(data.temp_password);
      }
      toast.success(`User ${email} ${data.is_new_user ? "created & attached" : "attached"}`);
      // Return the new user_id + a synthetic member row for the parent
      // dropdown so it renders without a full refetch.
      onCreated(data.user_id, { user_id: data.user_id, user: { email, name } });
    } catch (e) {
      toast.error(apiErrorMessage(e, "Could not create user"));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg rounded-none" data-testid="emp-create-link-user-dialog">
        <DialogHeader><DialogTitle>Create &amp; link a user account</DialogTitle></DialogHeader>
        {tempPassword ? (
          <div className="space-y-3 py-2">
            <div className="border border-emerald-500/40 bg-emerald-500/5 p-3 text-sm">
              <div className="font-semibold text-emerald-700 mb-1">User created</div>
              <div className="text-xs text-muted-foreground">A temporary password was generated. Share it securely with the employee (this is the only time it will be shown):</div>
              <div className="mt-2 font-mono text-sm p-2 bg-background border border-border select-all" data-testid="emp-temp-password">{tempPassword}</div>
            </div>
            <div className="flex justify-end">
              <Button onClick={onClose} className="rounded-none">Done</Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3 py-2">
            <p className="text-xs text-muted-foreground">
              This will create a user in this workspace with a temporary password and link it to this employee record so they can log in and see their assignments.
            </p>
            <div>
              <Label className="label-eyebrow mb-1 inline-block">Email</Label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="jane@company.com" data-testid="emp-create-link-email" />
            </div>
            <div>
              <Label className="label-eyebrow mb-1 inline-block">Name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Jane Doe" data-testid="emp-create-link-name" />
            </div>
            <div>
              <Label className="label-eyebrow mb-1 inline-block">Role</Label>
              <select value={role} onChange={(e) => setRole(e.target.value)} className="h-10 w-full border border-border rounded-none px-2 text-sm bg-background" data-testid="emp-create-link-role">
                {roles.map((r) => (
                  <option key={r.slug} value={r.slug}>{r.name} · {(r.permissions || []).length} perms</option>
                ))}
              </select>
            </div>
            <label className="flex items-center gap-2 text-xs">
              <input type="checkbox" checked={sendInvite} onChange={(e) => setSendInvite(e.target.checked)} data-testid="emp-create-link-invite" />
              Send them an invite email with the temporary password
            </label>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" className="rounded-none" onClick={onClose}>Cancel</Button>
              <Button onClick={submit} disabled={busy} className="rounded-none" data-testid="emp-create-link-submit">
                {busy ? "Creating…" : "Create & link"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Section({ icon: Icon, title, children }) {
  return (
    <section className="border border-border p-4 space-y-3">
      <div className="label-eyebrow inline-flex items-center gap-2">{Icon && <Icon className="w-3.5 h-3.5" />} {title}</div>
      <div className="space-y-3">{children}</div>
    </section>
  );
}
function Row2({ children }) { return <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">{children}</div>; }
function Row3({ children }) { return <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">{children}</div>; }
function Field({ label, value, onChange, type = "text", placeholder, testid }) {
  return (
    <div>
      <Label className="block mb-1.5 text-sm font-semibold">{label}</Label>
      <Input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="h-10" data-testid={testid} />
    </div>
  );
}
function SelectField({ label, value, onChange, options, testid, placeholder }) {
  return (
    <div>
      <Label className="block mb-1.5 text-sm font-semibold">{label}</Label>
      <Select value={value || "_none"} onValueChange={(v) => onChange(v === "_none" ? "" : v)}>
        <SelectTrigger className="h-10" data-testid={testid}><SelectValue placeholder={placeholder || "—"} /></SelectTrigger>
        <SelectContent>
          <SelectItem value="_none">{placeholder || "—"}</SelectItem>
          {options.map((o) => <SelectItem key={o.v} value={o.v}>{o.label}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}


// ═════════════════════════════════════════════════════════════════
//  Iter Feb2026 · Bulk-edit dialog
// ═════════════════════════════════════════════════════════════════

function EmployeesBulkEditDialog({ wsId, selectedIds, onClose, onSaved }) {
  const [departments, setDepartments] = useState([]);
  const [managers, setManagers] = useState([]);
  const [departmentId, setDepartmentId] = useState("");
  const [reportingManagerId, setReportingManagerId] = useState("");
  const [status, setStatus] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!wsId) return;
    (async () => {
      try {
        const [d, e] = await Promise.all([
          api.get(`/workspaces/${wsId}/departments`),
          api.get(`/workspaces/${wsId}/employees`),
        ]);
        setDepartments(d.data || []);
        setManagers((e.data || []).filter((r) => r.status !== "deleted"));
      } catch (err) {
        toast.error(apiErrorMessage(err, "Failed to load lookups"));
      }
    })();
  }, [wsId]);

  const save = async () => {
    const patch = {};
    if (departmentId) patch.department_id = departmentId;
    if (reportingManagerId) patch.reporting_manager_id = reportingManagerId;
    if (status) patch.status = status;
    if (Object.keys(patch).length === 0) {
      toast.error("Pick at least one field to change");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/workspaces/${wsId}/employees/bulk-edit`, {
        employee_ids: selectedIds, patch,
      });
      toast.success(`Updated ${data.updated} · Skipped ${data.skipped}`);
      onSaved();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Bulk edit failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="employees-bulk-edit-dialog">
        <DialogHeader>
          <DialogTitle>Bulk edit {selectedIds.length} employee{selectedIds.length === 1 ? "" : "s"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-xs text-muted-foreground">
            Leave a field blank to leave it unchanged. Bulk edits are audited per row and cannot be undone in a single click.
          </p>
          <div>
            <Label className="text-xs">Department</Label>
            <Select value={departmentId} onValueChange={setDepartmentId}>
              <SelectTrigger className="h-9" data-testid="bulk-edit-department">
                <SelectValue placeholder="— No change —" />
              </SelectTrigger>
              <SelectContent>
                {departments.map((d) => (
                  <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Reporting manager</Label>
            <Select value={reportingManagerId} onValueChange={setReportingManagerId}>
              <SelectTrigger className="h-9" data-testid="bulk-edit-manager">
                <SelectValue placeholder="— No change —" />
              </SelectTrigger>
              <SelectContent className="max-h-64">
                {managers.map((m) => (
                  <SelectItem key={m.id} value={m.id}>
                    {m.full_name || m.email_official || m.email} {m.designation ? `· ${m.designation}` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Active status</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="h-9" data-testid="bulk-edit-status">
                <SelectValue placeholder="— No change —" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="notice">On notice</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
            <Button onClick={save} disabled={busy} data-testid="bulk-edit-save">
              {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
              Apply to {selectedIds.length}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}


// ═════════════════════════════════════════════════════════════════
//  Feb 2026 · Bulk Shift-assign dialog
//    HR picks a shift and applies it to the already-selected employees.
//    Uses the existing POST /shifts/{sid}/assign endpoint which keys on
//    `user_id` — so we filter rows by employee.id first then pluck the
//    user ids. Rows missing a user_id (unlinked employee records) are
//    skipped with a warning instead of silently failing.
// ═════════════════════════════════════════════════════════════════

function EmployeesBulkShiftDialog({ wsId, selectedRows, onClose, onSaved }) {
  const [shifts, setShifts] = useState([]);
  const [shiftId, setShiftId] = useState("");
  const [busy, setBusy] = useState(false);

  const userIds = (selectedRows || []).map((r) => r.user_id).filter(Boolean);
  const skipped = (selectedRows || []).length - userIds.length;

  useEffect(() => {
    if (!wsId) return;
    (async () => {
      try {
        const { data } = await api.get(`/workspaces/${wsId}/shifts`);
        const list = data || [];
        setShifts(list);
        const def = list.find((s) => s.is_default) || list[0];
        if (def) setShiftId(def.id);
      } catch (err) {
        toast.error(apiErrorMessage(err, "Failed to load shifts"));
      }
    })();
  }, [wsId]);

  const save = async () => {
    if (!shiftId) { toast.error("Pick a shift"); return; }
    if (userIds.length === 0) { toast.error("No linked employees selected"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/shifts/${shiftId}/assign`, { user_ids: userIds });
      toast.success(`Shift assigned to ${data?.assigned ?? userIds.length} employee${(data?.assigned ?? userIds.length) === 1 ? "" : "s"}`);
      onSaved();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Assign shift failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="employees-bulk-shift-dialog">
        <DialogHeader>
          <DialogTitle>Set shift for {userIds.length} employee{userIds.length === 1 ? "" : "s"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-xs text-muted-foreground">
            The chosen shift overwrites the current shift on every selected employee. Shift timings drive the Daily plan capacity bands and the Now dashboard.
            {skipped > 0 && (
              <span className="block mt-1 text-amber-700" data-testid="bulk-shift-skip-note">
                {skipped} selected row{skipped === 1 ? "" : "s"} will be skipped — no linked user account.
              </span>
            )}
          </p>
          <div>
            <Label className="text-xs">Shift</Label>
            <Select value={shiftId} onValueChange={setShiftId}>
              <SelectTrigger className="h-9" data-testid="bulk-shift-select">
                <SelectValue placeholder="Choose a shift" />
              </SelectTrigger>
              <SelectContent>
                {shifts.map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.name} · {s.start_time}–{s.end_time}
                    {s.is_default ? " (default)" : ""}
                  </SelectItem>
                ))}
                {shifts.length === 0 && (
                  <div className="px-2 py-3 text-xs text-muted-foreground">
                    No shifts defined. Create one in HR → Attendance → Shifts first.
                  </div>
                )}
              </SelectContent>
            </Select>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
            <Button onClick={save} disabled={busy || !shiftId || userIds.length === 0} data-testid="bulk-shift-save">
              {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
              Apply to {userIds.length}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
